import {BrowserModule} from '@angular/platform-browser';
import {CUSTOM_ELEMENTS_SCHEMA, NgModule} from '@angular/core';

import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {LoginPageComponent} from './pages/login-page/login-page.component';
import {EquipmentPageComponent} from './pages/equipment-page/equipment-page.component';
import {ScottyLayoutComponent} from './layout/scotty-layout/scotty-layout.component';
import {NotFoundComponent} from './pages/not-found/not-found.component';
import {DashboardComponent} from './pages/dashboard/dashboard.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatCardModule} from '@angular/material/card';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {HeaderComponent} from './components/header/header.component';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatSidenavModule} from '@angular/material/sidenav';
import {SidebarElementComponent} from './components/sidebar-element/sidebar-element.component';
import {MatIconModule} from '@angular/material/icon';
import {ScottyCardComponent} from './components/scotty-card/scotty-card.component';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatTableModule} from '@angular/material/table';
import {MatSortModule} from '@angular/material/sort';
import {TableComponent} from './components/table/table.component';
import {CreateEquipmentPageComponent} from './pages/equipment-page/create-equipment-page/create-equipment-page.component';
import {AuthoringSlotComponent} from './components/authoring-slot/authoring-slot.component';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {AngularFireModule} from '@angular/fire';
import {environment} from '@environments/environment';
import {ListViewElementComponent} from './components/list-view-element/list-view-element.component';
import {MatChipsModule} from '@angular/material/chips';
import {ScottySpinnerComponent} from './components/scotty-spinner/scotty-spinner.component';
import {MatStepperModule} from '@angular/material/stepper';
import {MatGridListModule} from '@angular/material/grid-list';
import {ContactComponent} from './components/contact/contact.component';
import {VideocallComponent} from './components/videocall/videocall.component';
import {NewTaskDialogComponent} from './components/dialogs/new-task-dialog/new-task-dialog.component';
import {MatDialogModule} from '@angular/material/dialog';
import {NgScrollbarModule} from 'ngx-scrollbar';
import {NewStepDialogComponent} from './components/dialogs/new-step-dialog/new-step-dialog.component';
import {ChatModuleModule} from '@app/chat-module.module';
import {registerLocaleData} from '@angular/common';
import localeIt from '@angular/common/locales/it';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {EcoFabSpeedDialModule} from '@ecodev/fab-speed-dial';
import {MaintenancePageComponent} from './pages/maintenance-page/maintenance-page.component';
import {MatTabsModule} from '@angular/material/tabs';
import {AssistanceRequestComponent} from './components/assistance-request/assistance-request.component';
import {AssistanceViewComponent} from './components/assistance-view/assistance-view.component';
import {MaintenanceSingleChatPageComponent} from './pages/maintenance-page/maintenance-single-chat-page/maintenance-single-chat-page.component';
import {PhysicalAssetPageComponent} from './pages/equipment-page/physical-asset-page/physical-asset-page.component';
import {ConfirmDialogComponent} from './components/dialogs/confirm-dialog/confirm-dialog.component';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {
  NgxMatDatetimePickerModule,
  NgxMatNativeDateModule,
  NgxMatTimepickerModule
} from '@angular-material-components/datetime-picker';
import {PersonnelPageComponent} from './pages/personnel-page/personnel-page.component';
import {CreatePersonnelPageComponent} from './pages/personnel-page/create-personnel-page/create-personnel-page.component';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {FieldServicePageComponent} from './pages/field-service-page/field-service-page.component';
import {CreateFieldServicePageComponent} from './pages/field-service-page/create-field-service-page/create-field-service-page.component';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {CalendarPageComponent} from './pages/calendar-page/calendar-page.component';
import {FullCalendarModule} from '@fullcalendar/angular'; // the main connector. must go first
import dayGridPlugin from '@fullcalendar/daygrid'; // a plugin
import interactionPlugin from '@fullcalendar/interaction';
import {NewEventDialogComponent} from './components/dialogs/new-event-dialog/new-event-dialog.component';
import {TrainingPageComponent} from './pages/training-page/training-page.component';
import {NgxMatSelectSearchModule} from 'ngx-mat-select-search';
import {EventLinkDialogComponent} from './components/dialogs/new-event-dialog/event-link-dialog/event-link-dialog.component';
import {TrainingPageAccessCodeComponent} from './pages/training-page/training-page-access-code/training-page-access-code.component';
import {AuthenticatedVideocallPageComponent} from './pages/authenticated-videocall-page/authenticated-videocall-page.component';
import {QRCodeModule} from 'angularx-qrcode';
import {QrDialogComponent} from './components/dialogs/qr-dialog/qr-dialog.component';
import {AuthenticatedVideocallClosedPageComponent} from './pages/authenticated-videocall-page/authenticated-videocall-closed-page/authenticated-videocall-closed-page.component';
import {VideoPlaybackComponent} from './components/video-playback/video-playback.component';
import {PrivacyPageComponent} from './pages/privacy-page/privacy-page.component';
import {ScottySpinnerOverlayComponent} from './components/scotty-spinner-overlay/scotty-spinner-overlay.component';
import {MediaGridComponent} from './components/media-grid/media-grid.component';
import {EditDialogComponent} from './components/dialogs/edit-dialog/edit-dialog.component';
import {SkillPageComponent} from './pages/skill-page/skill-page.component';
import {HtmlSanitizer} from './pipe/html-sanitizer';
import {NewSkillDialogComponent} from './components/dialogs/new-skill-dialog/new-skill-dialog.component';
import {ColorPickerModule} from 'ngx-color-picker';
import {NewSkillLevelDialogComponent} from './components/dialogs/new-skill-level-dialog/new-skill-level-dialog.component';
import {BindSkillDialogComponent} from './components/dialogs/bind-skill-dialog/bind-skill-dialog.component';
import {ChooseResourceDialogComponent} from './components/dialogs/choose-resource-dialog/choose-resource-dialog.component';
import {InfoDialogComponent} from './components/dialogs/info-dialog/info-dialog.component';
import {ACCOUNT_MANAGER_SERVICE} from './services/account-manager/injection-token';
import {ParseAccountManagerService} from './services/account-manager/parse-account-manager-service';
import {FirebaseAccountManagerService} from './services/account-manager/firebase-account-manager-service';
import {DragDropModule} from '@angular/cdk/drag-drop';
import {CalendarEventDetailPageComponent} from './pages/calendar-page/calendar-event-detail-page/calendar-event-detail-page.component';
import {TimelineComponent} from './components/timeline/timeline.component';
import {MatExpansionModule} from '@angular/material/expansion';
import {BindFieldServiceDialogComponent} from './components/dialogs/bind-field-service-dialog/bind-field-service-dialog.component';
import {TimelineElementComponent} from './components/timeline/timeline-element/timeline-element.component';
import {MatDividerModule} from '@angular/material/divider';
import {SettingsPageComponent} from './pages/settings-page/settings-page.component';
import {DetailCardComponent} from './components/detail-card/detail-card.component';
import {SearchBarComponent} from './components/search-bar/search-bar.component';
import {GridElementComponent} from './components/grid-element/grid-element.component';
import {EditResourceDialogComponent} from './components/dialogs/edit-resource-dialog/edit-resource-dialog.component';
import {NgxMatFileInputModule} from '@angular-material-components/file-input';
import {TranslateLoader, TranslateModule, TranslatePipe} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import { EditorToolsComponent } from './components/videocall/editor-tools/editor-tools.component'; // a plugin
import {InputDialogComponent} from "./components/dialogs/input-dialog/input-dialog.component";
import { ProcedurePageComponent } from './pages/procedure-page/procedure-page.component';
import {MatTooltipModule} from '@angular/material/tooltip';
import { VideocallUnityComponent } from './components/videocall/videocall-providers/videocall-unity/videocall-unity.component';
import { VideocallBabylonComponent } from './components/videocall/videocall-providers/videocall-babylon/videocall-babylon.component';
import {OAuthModule, OAuthStorage} from 'angular-oauth2-oidc';
import {
  KeycloakAccountManagerService
} from "./services/account-manager/keycloak-account-manager.service";
import { CartographicConsolePageComponent } from './pages/cartographic-console-page/cartographic-console-page.component';
import { VideocallV2Component } from './components/videocall-v2/videocall-v2.component';

registerLocaleData(localeIt, environment.defaultDateLocale);

FullCalendarModule.registerPlugins([ // register FullCalendar plugins
  dayGridPlugin,
  interactionPlugin
]);

export function storageFactory() : OAuthStorage {
  return localStorage;
}

// Initialize firebase module if required
const firebaseModule = environment.authProvider == 'parse' ? [] : [AngularFireModule.initializeApp(environment.firebaseConfig)];

// Getter for account manager service for Firebase / Parse / KeyCloak
function getAuthProvider() {
  switch (environment.authProvider) {
    case 'firebase':
      return FirebaseAccountManagerService;
    case 'parse':
      return ParseAccountManagerService;
    case 'keycloak':
      return KeycloakAccountManagerService;
    default:
      throw new Error('Not Implemented');
  }
}

@NgModule({
  declarations: [
    AppComponent,
    LoginPageComponent,
    EquipmentPageComponent,
    ScottyLayoutComponent,
    NotFoundComponent,
    DashboardComponent,
    HeaderComponent,
    SidebarElementComponent,
    ScottyCardComponent,
    TableComponent,
    CreateEquipmentPageComponent,
    AuthoringSlotComponent,
    ListViewElementComponent,
    ScottySpinnerComponent,
    ContactComponent,
    VideocallComponent,
    NewTaskDialogComponent,
    NewStepDialogComponent,
    MaintenancePageComponent,
    AssistanceRequestComponent,
    AssistanceViewComponent,
    MaintenanceSingleChatPageComponent,
    PhysicalAssetPageComponent,
    ConfirmDialogComponent,
    PersonnelPageComponent,
    CreatePersonnelPageComponent,
    FieldServicePageComponent,
    CreateFieldServicePageComponent,
    CalendarPageComponent,
    NewEventDialogComponent,
    TrainingPageComponent,
    EventLinkDialogComponent,
    TrainingPageAccessCodeComponent,
    AuthenticatedVideocallPageComponent,
    QrDialogComponent,
    AuthenticatedVideocallClosedPageComponent,
    VideoPlaybackComponent,
    PrivacyPageComponent,
    ScottySpinnerOverlayComponent,
    MediaGridComponent,
    EditDialogComponent,
    SkillPageComponent,
    HtmlSanitizer,
    NewSkillDialogComponent,
    NewSkillLevelDialogComponent,
    BindSkillDialogComponent,
    ChooseResourceDialogComponent,
    InfoDialogComponent,
    CalendarEventDetailPageComponent,
    TimelineComponent,
    BindFieldServiceDialogComponent,
    TimelineElementComponent,
    SettingsPageComponent,
    DetailCardComponent,
    SearchBarComponent,
    GridElementComponent,
    EditResourceDialogComponent,
    EditorToolsComponent,
    InputDialogComponent,
    ProcedurePageComponent,
    VideocallUnityComponent,
    VideocallBabylonComponent,
    CartographicConsolePageComponent,
    VideocallBabylonComponent,
    VideocallV2Component
  ],
  imports: [
    BrowserModule,
    ...firebaseModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatCardModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatToolbarModule,
    MatSidenavModule,
    MatIconModule,
    MatPaginatorModule,
    MatTableModule,
    MatSortModule,
    MatSnackBarModule,
    MatChipsModule,
    MatStepperModule,
    ReactiveFormsModule,
    MatGridListModule,
    MatDialogModule,
    NgScrollbarModule,
    ChatModuleModule,
    HttpClientModule,
    EcoFabSpeedDialModule,
    MatTabsModule,
    MatDatepickerModule,
    NgxMatTimepickerModule,
    NgxMatDatetimePickerModule,
    NgxMatNativeDateModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    FullCalendarModule,
    NgxMatSelectSearchModule,
    QRCodeModule,
    ColorPickerModule,
    DragDropModule,
    MatExpansionModule,
    MatDividerModule,
    NgxMatFileInputModule,
    MatTooltipModule,
    OAuthModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient]
      }
    })
  ],
  providers: [
    {
      provide: ACCOUNT_MANAGER_SERVICE,
      useClass: getAuthProvider()
    },
    { provide: OAuthStorage, useFactory: storageFactory },
    TranslatePipe
    /*{
      provide: HTTP_INTERCEPTORS,
      useClass: ContentTypeInterceptor,
      multi: true
    }*/
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}

export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
